/**
 * @file PermissionsContext.tsx
 * @brief Global permissions context for dynamic RBAC access control
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { permissionService, type PagePermission, type FeaturePermission } from '@/services/api/permissionService';
import { useAppSelector } from '@/store';
import { selectIsAuthenticated } from '@/store/slices/authSlice';

interface PermissionsContextType {
  canAccessPage: (pageKey: string) => boolean;
  isPageAccessLoading: boolean;
  hasFeaturePermission: (pageKey: string, featureKey: string, action: string) => boolean;
  isFeaturePermissionsLoading: boolean;
  isPermissionsReady: boolean;
  permissionsError: boolean;
  refetchPermissions: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

export const usePageAccess = (pageKey: string): boolean => {
  const { canAccessPage } = usePermissions();
  return canAccessPage(pageKey);
};

export const useFeaturePermission = (
  pageKey: string,
  featureKey: string,
  action: string
): boolean => {
  const { hasFeaturePermission, isFeaturePermissionsLoading, isPermissionsReady } = usePermissions();
  if (!isPermissionsReady || isFeaturePermissionsLoading) {
    return false;
  }
  return hasFeaturePermission(pageKey, featureKey, action);
};

export const useActionPermission = (
  pageKey: string,
  featureKey: string,
  action: string
): { allowed: boolean; loading: boolean } => {
  const { canAccessPage, hasFeaturePermission, isPermissionsReady } = usePermissions();

  const allowed = canAccessPage(pageKey) && hasFeaturePermission(pageKey, featureKey, action);

  return { allowed, loading: !isPermissionsReady };
};

interface PermissionsProviderProps {
  children: React.ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const hasAuthToken = !!Cookies.get('authToken');
  const shouldFetchPermissions = isAuthenticated || hasAuthToken;

  const {
    data: pagePermissions,
    isLoading: isPageAccessLoading,
    isError: isPagePermError,
    refetch: refetchPagePermissions,
  } = useQuery({
    queryKey: ['permissions', 'pages', 'me'],
    queryFn: () => permissionService.getMyPagePermissions(),
    enabled: shouldFetchPermissions,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const {
    data: featurePermissions,
    isLoading: isFeaturePermissionsLoading,
    isError: isFeaturePermError,
    refetch: refetchFeaturePermissions,
  } = useQuery({
    queryKey: ['permissions', 'features', 'me'],
    queryFn: () => permissionService.getMyFeaturePermissions(),
    enabled: shouldFetchPermissions,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const pageAccessMap = useMemo(() => {
    if (!pagePermissions) return new Map<string, boolean>();
    const map = new Map<string, boolean>();
    pagePermissions.forEach((permission: PagePermission) => {
      const pageKey = String(
        (permission as PagePermission).PAGE_KEY ??
          (permission as unknown as { pageKey?: string }).pageKey ??
          ''
      );
      if (!pageKey) return;
      const raw =
        (permission as PagePermission).CAN_ACCESS ??
        (permission as unknown as { canAccess?: unknown }).canAccess;
      map.set(pageKey, raw === true || raw === 1 || raw === '1');
    });
    return map;
  }, [pagePermissions]);

  const featurePermMap = useMemo(() => {
    if (!featurePermissions) return new Map<string, boolean>();
    const map = new Map<string, boolean>();
    featurePermissions.forEach((permission: FeaturePermission) => {
      const pageKey = String(
        permission.PAGE_KEY ?? (permission as unknown as { pageKey?: string }).pageKey ?? ''
      );
      const featureKey = String(
        permission.FEATURE_KEY ??
          (permission as unknown as { featureKey?: string }).featureKey ??
          ''
      );
      const action = String(
        permission.ACTION ?? (permission as unknown as { action?: string }).action ?? ''
      );
      const raw =
        permission.GRANTED ?? (permission as unknown as { granted?: unknown }).granted;
      const key = `${pageKey}:${featureKey}:${action}`;
      map.set(key, raw === true || raw === 1 || raw === '1');
    });
    return map;
  }, [featurePermissions]);

  const pagePermissionsLoaded = !!pagePermissions;
  const featurePermissionsLoaded = !!featurePermissions;
  const permissionsLoaded = pagePermissionsLoaded && featurePermissionsLoaded;
  const permissionsError = isPagePermError || isFeaturePermError;

  const canAccessPage = useCallback(
    (pageKey: string): boolean => {
      if (isPageAccessLoading) return false;
      if (!pagePermissionsLoaded) return false;
      const hasAccess = pageAccessMap.get(pageKey);
      if (hasAccess !== undefined) return hasAccess;
      return false;
    },
    [pageAccessMap, isPageAccessLoading, pagePermissionsLoaded]
  );

  const hasFeaturePermission = useCallback(
    (pageKey: string, featureKey: string, action: string): boolean => {
      if (isFeaturePermissionsLoading) return false;
      if (!featurePermissionsLoaded) return false;
      const key = `${pageKey}:${featureKey}:${action}`;
      const granted = featurePermMap.get(key);
      if (granted !== undefined) return granted;
      return false;
    },
    [featurePermMap, isFeaturePermissionsLoading, featurePermissionsLoaded]
  );

  const refetchPermissions = useCallback(() => {
    refetchPagePermissions();
    refetchFeaturePermissions();
  }, [refetchPagePermissions, refetchFeaturePermissions]);

  const isPermissionsReady =
    !shouldFetchPermissions ||
    (!isPageAccessLoading && !isFeaturePermissionsLoading && (permissionsLoaded || permissionsError));

  const value: PermissionsContextType = useMemo(
    () => ({
      canAccessPage,
      isPageAccessLoading,
      hasFeaturePermission,
      isFeaturePermissionsLoading,
      isPermissionsReady,
      permissionsError,
      refetchPermissions,
    }),
    [
      canAccessPage,
      isPageAccessLoading,
      hasFeaturePermission,
      isFeaturePermissionsLoading,
      isPermissionsReady,
      permissionsError,
      refetchPermissions,
    ]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
